<script setup>
import { convertToInr } from "@/services/common-services";
import { useUserStore } from "@/store/user-store";
import { Column, DataTable } from "primevue";
import { computed, onMounted, nextTick, ref } from "vue";
import ManageLiabilityDialog from "@/components/dashboard/ManageLiabilityDialog.vue";
import ManageIncomeDialog from "@/components/dashboard/ManageIncomeDialog.vue";
import ManageExpenseDialog from "@/components/dashboard/ManageExpenseDialog.vue";
import AnLoader from '@/components/common/AnLoader.vue';

const store = useUserStore();

const data = computed(() => store.financialStatement);
const assetData = computed(() => data.value?.customerAssets || []);
const liabilityData = computed(() => data.value?.customerLiabilities || []);
const expenseData = computed(() => data.value?.customerExpenses || []);
const incomeData = computed(() => data.value?.customerIncome || []);
const totalExpense = computed(() =>
  expenseData.value.reduce((total, item) => total + item.expAmt, 0)
);
const totalIncome = computed(() =>
  incomeData.value.reduce((total, item) => total + item.incomeAmt, 0)
);
const totalAsset = computed(() =>
  assetData.value.reduce((total, item) => total + item.assetValue, 0)
);
const totalLiability = computed(() =>
  liabilityData.value.reduce((total, item) => total + item.amtOutstanding, 0)
);

const showLiabilityDialog = ref(false);
const showIncomeDialog = ref(false);
const showExpenseDialog = ref(false);
const isLoadingFS = ref(false);

onMounted(() => {
  nextTick(() => {
    const headers = document.querySelectorAll(
      'td[data-pc-section="rowgroupheadercell"]'
    );
    headers.forEach((td) => {
      td.setAttribute("colspan", "2");
    });
  });
});

const reloadFinancialStatement = async () => {
  isLoadingFS.value = true;
  await store.loadFinancialStatement(store.dashboardData.customerId);
  isLoadingFS.value = false;
};

const handleDialogAdd = async (type, payload) => {
  await store.updateFinancialStatement(payload);
  await reloadFinancialStatement();
};
</script>

<template>
  <div class="d-flex flex-direction-column mt-30">
    <AnLoader v-if="isLoadingFS" text="Financial Statement is loading..." />
    <template v-else>
      <span class="font-20 font-weight-700">Balance Sheet</span>
      <div class="table-grid d-grid mt-20">
        <DataTable
          :value="assetData"
          :pt="{
            headerRow: { class: 'font-weight-500' },
            bodyRow: { class: 'font-15 font-weight-500' },
          }"
          class="left-table pr-10"
        >
          <Column header="Asset" body-class="text-primary">
            <template #body="props"> {{ props.data.assetName }}</template>
          </Column>
          <Column class="text-end">
            <template #header>
              <span class="w-100 text-end">Value</span>
            </template>
            <template #body="props">
              {{ convertToInr(props.data.assetValue) }}</template
            >
          </Column>
          <template #footer>
            <table class="w-100 bg-primary text-white">
              <tbody>
                <tr>
                  <td colspan="1" class="font-weight-600 p-10">
                    Total Asset Value
                  </td>
                  <td colspan="1" class="font-weight-600 p-10 text-end">
                    {{ convertToInr(totalAsset) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </DataTable>
        <DataTable
          :value="liabilityData"
          :pt="{
            headerRow: { class: 'font-weight-500' },
            bodyRow: { class: 'font-15 font-weight-500' },
          }"
          class="right-table pl-10"
        >
          <Column>
            <template #header>
              <div class="d-flex align-items-center justify-between">
                <span class="text-white">Liability</span> &nbsp;&nbsp;
                <button class="manage-btn" @click="showLiabilityDialog = true">
                    Update
                </button>
              </div>
            </template>
            <template #body="props"> {{ props.data.liabilityNm }}</template>
          </Column>
          <Column class="text-end">
            <template #header>
              <span class="w-100 text-end">Value</span>
            </template>
            <template #body="props">
              {{ convertToInr(props.data.amtOutstanding) }}</template
            >
          </Column>
          <template #footer>
            <table class="w-100 bg-primary text-white">
              <tbody>
                <tr>
                  <td colspan="1" class="font-weight-600 p-10">
                    Total Liability Value
                  </td>
                  <td colspan="1" class="font-weight-600 p-10 text-end">
                    {{ convertToInr(totalLiability) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </DataTable>
      </div>
      <span class="font-20 font-weight-700 mt-30">Income & Expenses</span>
      <div class="table-grid d-grid mt-20">
        <DataTable
          :value="incomeData"
          rowGroupMode="subheader"
          groupRowsBy="incomeHd"
          class="left-table pr-10"
          sortField="incomeHd"
          :sortOrder="1"
          :pt="{
            headerRow: { class: 'font-weight-500' },
            bodyRow: {
              class: 'font-15 font-weight-500',
            },
            rowGroupHeaderCell: {
              class: 'font-20 font-weight-600 text-primary',
              style: { padding: '5px !important' },
            },
          }"
        >
          <template #groupheader="slotProps">
            {{ slotProps.data.incomeHd }}
          </template>
          <Column>
            <template #header>
              <div class="d-flex align-items-center justify-between">
                <span class="text-white">Income</span>&nbsp;&nbsp;
                <button class="manage-btn" @click="showIncomeDialog = true">
                  Update
                </button>
              </div>
            </template>
            <template #body="props">{{ props.data.incomeTyp }}</template>
          </Column>
          <Column class="text-end">
            <template #header>
              <span class="w-100 text-end">Amount / Month</span>
            </template>
            <template #body="props">
              {{ convertToInr(props.data.incomeAmt) }}</template
            >
          </Column>
          <template #footer>
            <table class="w-100 bg-primary text-white">
              <tbody>
                <tr>
                  <td colspan="1" class="font-weight-600 p-10">Total Income</td>
                  <td colspan="1" class="font-weight-600 p-10 text-end">
                    {{ convertToInr(totalIncome) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </DataTable>
        <DataTable
          :value="expenseData"
          rowGroupMode="subheader"
          groupRowsBy="expHead"
          sortField="expHead"
          :sortOrder="1"
          class="right-table pl-10"
          :pt="{
            headerRow: { class: 'font-weight-500' },
            bodyRow: { class: 'font-15 font-weight-500' },
            rowGroupHeaderCell: {
              class: 'font-20 font-weight-600 text-primary',
              style: { padding: '5px !important' },
            },
          }"
        >
          <template #groupheader="slotProps">
            {{ slotProps.data.expHead }}
          </template>
          <Column>
            <template #header>
              <div class="d-flex align-items-center justify-between">
                <span class="text-white">Expense</span>&nbsp;&nbsp;
                <button class="manage-btn" @click="showExpenseDialog = true">
                  Update
                </button>
              </div>
            </template>
            <template #body="props">{{ props.data.expType }}</template>
          </Column>
          <Column class="text-end">
            <template #header>
              <span class="w-100 text-end">Amount / Month</span>
            </template>
            <template #body="props">
              {{ convertToInr(props.data.expAmt) }}</template
            >
          </Column>
          <template #footer>
            <table class="w-100 bg-primary text-white">
              <tbody>
                <tr>
                  <td colspan="1" class="font-weight-600 p-10">Total Expense</td>
                  <td colspan="1" class="font-weight-600 p-10 text-end">
                    {{ convertToInr(totalExpense) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </DataTable>
      </div>
      <!-- Dialogs -->
      <ManageLiabilityDialog v-model="showLiabilityDialog" />
      <ManageIncomeDialog v-model="showIncomeDialog" />
      <ManageExpenseDialog v-model="showExpenseDialog" />
    </template>
  </div>
</template>

<style scoped>
.table-grid {
  grid-template-columns: repeat(2, 1fr);
}

.left-table {
  border-right: 0.5px solid var(--an-gray1);
}
.right-table {
  border-left: 0.5px solid var(--an-gray1);
}

.divider {
  height: 1px;
  background-color: var(--an-gray1);
}

.manage-btn {
  background: var(--an-primary);
  color: #fff;
  border: 2.5px solid #fff;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  margin-left: 8px;
  transition: background 0.15s;
}
.manage-btn:hover {
  background: #0a3c4c;
}

@media (max-width: 900px) {
  .table-grid {
    grid-template-columns: 1fr !important;
  }
  .left-table,
  .right-table {
    border: none !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
</style>
